import type {
  AgentExecutionStreamCache,
  AgentExecutionStreamEvent,
} from '~/features/ask-anything/agentic-ai/types';

import {
  createStreamingMessage,
  getStreamEndpoint,
} from '~/features/ask-anything/agentic-ai/utils';
import { SSE_STREAM_TIMEOUT } from '~/features/ask-anything/chatbot/constants';
import { createSseConnection } from '~/features/ask-anything/chatbot/hooks/sseConnection';
import { intlService } from '~/i18n/IntlService';

import { transformCamelizedStreamMessage } from './transformers';

const getErrorMessage = (id: string, defaultMessage: string): string => {
  try {
    return intlService.getIntl().formatMessage({ id, defaultMessage });
  } catch {
    return defaultMessage;
  }
};

export const INITIAL_STREAM_STATE: AgentExecutionStreamCache = {
  streamMessage: null,
  isStreaming: false,
  thinkingEvents: [],
  messages: [],
  error: null,
};

export interface StreamAgentExecutionArgs {
  executionId: string;
  method?: 'GET' | 'POST';
}

// How many times to silently reconnect when the connection drops mid-execution.
// Each attempt waits RECONNECT_DELAY_MS before opening a new connection.
// Reconnects are skipped when stream_end was already received or the connection
// was intentionally aborted (e.g. user cancelled, cache evicted).
export const MAX_RECONNECT_ATTEMPTS = 5;
export const RECONNECT_DELAY_MS = 3_000;

/**
 * RTK Query `onCacheEntryAdded` handler for agent execution SSE streaming.
 *
 * Opens an SSE connection when the cache entry is created and keeps it alive
 * independent of component lifecycle. The connection is cleaned up only when
 * the cache entry is garbage collected (after `keepUnusedDataFor` expires).
 *
 * If the connection closes before a `stream_end` event is received, it
 * automatically reconnects up to MAX_RECONNECT_ATTEMPTS times with a short
 * delay between each attempt. This recovers from mid-execution network drops
 * where the backend is still running.
 */
export const onStreamCacheEntryAdded = async (
  arg: StreamAgentExecutionArgs,
  {
    updateCachedData,
    cacheDataLoaded,
    cacheEntryRemoved,
  }: {
    updateCachedData: (
      updater: (draft: AgentExecutionStreamCache) => void,
    ) => void;
    cacheDataLoaded: Promise<unknown>;
    cacheEntryRemoved: Promise<void>;
  },
) => {
  await cacheDataLoaded;

  const url = getStreamEndpoint(arg.executionId);
  const method = arg.method ?? 'GET';

  let receivedStreamEnd = false;
  let reconnectAttempts = 0;
  let reconnectTimer: ReturnType<typeof setTimeout> | undefined;
  // Ref object prevents TypeScript from narrowing the type to `never` after an
  // await in an async function that also has closures reassigning the variable.
  const abortControllerRef: { current: AbortController | null } = {
    current: null,
  };

  const openConnection = () => {
    abortControllerRef.current = createSseConnection(
      url,
      {
        onMessage: (event: unknown) => {
          const streamEvent = event as AgentExecutionStreamEvent;

          updateCachedData((draft) => {
            switch (streamEvent.eventType) {
              case 'thinking':
                if (streamEvent.text) {
                  draft.thinkingEvents.push(streamEvent.text);
                }
                break;

              case 'message_start':
                draft.streamMessage = createStreamingMessage(arg.executionId);
                break;

              case 'message_delta':
                if (draft.streamMessage && streamEvent.text) {
                  draft.streamMessage.text += streamEvent.text;
                }
                break;

              case 'message': {
                if (streamEvent.message) {
                  const transformedMsg = transformCamelizedStreamMessage(
                    streamEvent.message,
                  );
                  draft.messages.push(transformedMsg);
                }
                break;
              }

              case 'message_end':
                if (streamEvent.message) {
                  const transformedMessage = transformCamelizedStreamMessage(
                    streamEvent.message,
                  );
                  draft.streamMessage = transformedMessage;
                }
                break;

              case 'stream_end':
                receivedStreamEnd = true;
                draft.isStreaming = false;
                draft.thinkingEvents = [];
                if (streamEvent.text) {
                  draft.error = streamEvent.text;
                }
                break;
            }
          });
        },
        onOpen: () => {
          updateCachedData((draft) => {
            draft.isStreaming = true;
            draft.thinkingEvents = [];
            draft.streamMessage = null;
            draft.error = null;
          });
        },
        onClose: () => {
          updateCachedData((draft) => {
            draft.isStreaming = false;
          });
          if (
            !receivedStreamEnd &&
            !abortControllerRef.current?.signal.aborted &&
            reconnectAttempts < MAX_RECONNECT_ATTEMPTS
          ) {
            reconnectAttempts += 1;
            reconnectTimer = setTimeout(openConnection, RECONNECT_DELAY_MS);
          }
        },
        onError: () => {
          receivedStreamEnd = true;
          updateCachedData((draft) => {
            draft.isStreaming = false;
            draft.error = getErrorMessage(
              'streamAgentExecution.streamingError',
              'Streaming error occurred.',
            );
          });
        },
        onAuthenticationError: () => {
          receivedStreamEnd = true;
          updateCachedData((draft) => {
            draft.isStreaming = false;
            draft.error = getErrorMessage(
              'streamAgentExecution.authenticationError',
              'Authentication error.',
            );
          });
        },
        onRateLimitError: () => {
          receivedStreamEnd = true;
          updateCachedData((draft) => {
            draft.isStreaming = false;
            draft.error = getErrorMessage(
              'streamAgentExecution.rateLimitError',
              'Rate limit exceeded.',
            );
          });
        },
        onTimeout: () => {
          updateCachedData((draft) => {
            draft.isStreaming = false;
            draft.error = getErrorMessage(
              'streamAgentExecution.timeoutError',
              'Stream timed out.',
            );
          });
        },
      },
      {
        method,
        headers: { 'Content-Type': 'application/json' },
        openWhenHidden: true,
        timeout: SSE_STREAM_TIMEOUT,
      },
    );
  };

  openConnection();

  // Wait for the cache entry to be garbage collected, then abort the connection
  await cacheEntryRemoved;
  clearTimeout(reconnectTimer);
  abortControllerRef.current?.abort();
};
